import { MessageBar, MessageBarType, Panel, PanelType, PrimaryButton } from '@fluentui/react';
import * as React from 'react';
import { useContext } from 'react';

import { EgressOptionEnum } from '../../../Common/CommonModels/EgressOptionEnum';
import { ViewPrivacyApprovalArtifacts } from '../../../Common/Components/ViewPrivacyApprovalArtifacts';
import { CDMRecord } from '../../../Common/DataOwnerModels/CDMRecord';
import { DataOwnerRequestContext } from '../../../Common/DataOwnerModels/DataOwnerRequestDetails';
import { DependentEntity, Rule, RuleSetRecord } from '../../../Common/DataOwnerModels/RuleSetRecord';
import * as Styled from '../../../Common/Styles/viewRequest.styled';
import * as styled from './DataOwnerManagement.styled';

export type ViewDataOwnerRequestPanelProps = {
  isOpen?: boolean;
  dismissPanel(): void;
};
export function ViewDataOwnerRequestPanel(props: ViewDataOwnerRequestPanelProps): React.ReactElement {
  const {dataOwnerRequestDetails} = useContext(DataOwnerRequestContext);
  const request = dataOwnerRequestDetails.request;
  const existingCDMList = dataOwnerRequestDetails.nonProdApprovedCdms.concat(
    dataOwnerRequestDetails.prodApprovedCdms,
    dataOwnerRequestDetails.cdms
  );

  const onRenderHeader = () => {
    return (
      <Styled.PanelHeaderContainer>
        <MessageBar messageBarType={MessageBarType.warning}>This view is read only.</MessageBar>
        <Styled.PanelHeaderTitleText>View Data Owner Request</Styled.PanelHeaderTitleText>
      </Styled.PanelHeaderContainer>
    );
  };

  const onRenderFooterContent = () => {
    return <PrimaryButton text="Close" onClick={props.dismissPanel} />;
  };

  const onRenderNavigationEmptyContent = () => {
    return <div></div>;
  };

  const retrieveEgressOptions = (egressOptions: number[]): string => {
    let egressOptionStr = '';
    egressOptions.forEach((x) => (egressOptionStr += EgressOptionEnum[x] + '; '));
    return egressOptionStr;
  };

  return (
    <Panel
      isOpen={props.isOpen}
      type={PanelType.medium}
      onDismiss={props.dismissPanel}
      onRenderHeader={onRenderHeader}
      onRenderFooterContent={onRenderFooterContent}
      onRenderNavigation={onRenderNavigationEmptyContent}
      hasCloseButton={false}
      isFooterAtBottom={true}
      isLightDismiss={true}
      styles={Styled.stickyFooterPanelStyles}
      data-testid="view-dataowner-request-panel"
    >
        <Styled.ViewRequestContainer>

          <Styled.BasicDetailsContainer>

            <Styled.SubSectionalHeading>
              <Styled.SubSectionalHeadingText>Request Details</Styled.SubSectionalHeadingText>
              <Styled.LineDivider />
            </Styled.SubSectionalHeading>

            <Styled.BasicDetailsSection>
              <Styled.BasicDetailsField>Request ID</Styled.BasicDetailsField>
              <Styled.BasicDetailsValue>{request.id}</Styled.BasicDetailsValue>
            </Styled.BasicDetailsSection>

            <Styled.BasicDetailsSection>
              <Styled.BasicDetailsField>Environment</Styled.BasicDetailsField>
              <Styled.BasicDetailsValue>{request.environment === 0 ? 'Non-Production' : 'Production'}</Styled.BasicDetailsValue>
            </Styled.BasicDetailsSection>

            <Styled.BasicDetailsSection>
              <Styled.BasicDetailsField>Request Name</Styled.BasicDetailsField>
              <Styled.BasicDetailsValue>{request.name}</Styled.BasicDetailsValue>
            </Styled.BasicDetailsSection>
 
            <Styled.BasicDetailsSection>
              <Styled.BasicDetailsField>Description</Styled.BasicDetailsField>
              <Styled.BasicDetailsValue>{request.description.length > 0 ? request.description : 'N/A'}</Styled.BasicDetailsValue>
            </Styled.BasicDetailsSection>

            <Styled.BasicDetailsSection>
              <Styled.BasicDetailsField>Privacy Approval Date</Styled.BasicDetailsField>
              <Styled.BasicDetailsValue>{request.privacyApprovalDate}</Styled.BasicDetailsValue>
            </Styled.BasicDetailsSection>

            <Styled.BasicDetailsSection>
              <Styled.BasicDetailsField>Additional Notes</Styled.BasicDetailsField>
              <Styled.BasicDetailsValue>{request.additionalNotes.length > 0 ? request.additionalNotes : 'N/A'}</Styled.BasicDetailsValue>
            </Styled.BasicDetailsSection>

            <Styled.BasicDetailsSection>
              <Styled.BasicDetailsField>Privacy Approval Artifacts</Styled.BasicDetailsField>

              {request.privacyApprovalArtifacts.length > 0 && (
                <ViewPrivacyApprovalArtifacts attachmentList={request.privacyApprovalArtifacts} />  
              )}

              {request.privacyApprovalArtifacts.length == 0 && (
                <Styled.BasicDetailsValue>{'N/A'}</Styled.BasicDetailsValue>
              )}

            </Styled.BasicDetailsSection>

          </Styled.BasicDetailsContainer>

          <Styled.CdmDetailsContainer>

            <Styled.SubSectionalHeading>
              <Styled.SubSectionalHeadingText>CDMs</Styled.SubSectionalHeadingText>
              <Styled.LineDivider />
            </Styled.SubSectionalHeading>

            {dataOwnerRequestDetails?.cdms?.length <= 0 && (
              <styled.ReviewTable>
                <tr>
                  <td colSpan={2}> No CDMs are defined </td>
                </tr>
                <br />
              </styled.ReviewTable>
            )}

            {dataOwnerRequestDetails?.cdms?.map((cdmObj: CDMRecord, cdmIndex) => (
              <>
                <styled.ReviewTable>
                  <tr>
                    <td style={{ width: '5%' }}>{cdmIndex + 1 + '.'}</td>
                    <th>CDM Name</th>
                    <td>{cdmObj.name}</td>
                  </tr>
                  <tr>
                    <td style={{ width: '5%' }}></td>
                    <th>Description</th>
                    <td>{cdmObj.description}</td>
                  </tr>
                  <tr>
                    <td style={{ width: '5%' }}></td>
                    <th>Egress Options</th>
                    <td>{retrieveEgressOptions(cdmObj.egressOptions)}</td>
                  </tr>
                </styled.ReviewTable>
                <br />
              </>
            ))}
          
          </Styled.CdmDetailsContainer>
          
          <Styled.RuleSetsDetailsContainer>

            <Styled.SubSectionalHeading>
              <Styled.SubSectionalHeadingText>Rule Sets</Styled.SubSectionalHeadingText>
              <Styled.LineDivider />
            </Styled.SubSectionalHeading>

            {dataOwnerRequestDetails?.ruleSets?.length <= 0 && (
              <styled.ReviewTable>
                <tr>
                  <td colSpan={2}> No Rule Sets are defined </td>
                </tr>
                <br />
              </styled.ReviewTable>
            )}

            {dataOwnerRequestDetails?.ruleSets?.map((ruleSetObj: RuleSetRecord, ruleSetIndex) => (
              <>
                <styled.ReviewTable>
                  <tr>
                    <td style={{ width: '5%' }}>{ruleSetIndex + 1 + '.'}</td>
                    <th>Rule Set Name</th>
                    <td>{ruleSetObj.name}</td>
                  </tr>
                  <tr>
                    <td style={{ width: '5%' }}></td>
                    <th>Rule Set Type</th>
                    <td>{ruleSetObj.ruleSetType}</td>
                  </tr>
                  <tr>
                    <td style={{ width: '5%' }}></td>
                    <th>Source CDM</th>
                    <td>{existingCDMList?.find((x) => x.id == ruleSetObj?.dataSource?.entityTypeId)?.name}</td>
                  </tr>
                  <tr>
                    <td style={{ width: '5%' }}></td>
                    <th>Dependent CDMs</th>
                    <td>
                      {ruleSetObj?.dataSource?.dependentEntities?.map(
                        (depCdm: DependentEntity) =>
                          existingCDMList?.find((x) => x.id == depCdm?.entityTypeId)?.name + '; \n'
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td style={{ width: '5%' }}></td>
                    <th>Rules</th>
                    <td>{ruleSetObj?.rules?.map((ruleObj: Rule) => ruleObj?.ruleName + '; \n')}</td>
                  </tr>
                </styled.ReviewTable>
                <br />
              </>
            ))}

          </Styled.RuleSetsDetailsContainer>

        </Styled.ViewRequestContainer>
    </Panel>
  );
}
